import {
  ImageView,
  LinearAddFilter,
  MultiplyFilter,
  TintFilter,
} from '@play-co/timestep-core/ui';
import { Texture } from './Texture';
import { Container } from './Container';
import { hex2string } from '../../utils';
import { getMatch2AssetPath } from '../../../game/util/assets';

export class Sprite extends Container {
  protected _innerView!: ImageView;
  protected _texture: Texture | null = null;
  // Tint uses the color multiply filter that looks more what happens in pixi
  protected _tint = 0xffffff;
  protected _tintFilter: MultiplyFilter | null = null;
  // To use actual timestep tint (cover the whole sprite with a color) use fill instead
  protected _fill: number | null = null;
  protected _fillFilter: TintFilter | null = null;
  // Filter to add a color to the sprite
  protected _linearAdd: number | null = null;
  protected _linearAddFilter: LinearAddFilter | null = null;

  public static from(texture: Texture | string) {
    return new Sprite(texture);
  }

  constructor(texture?: Texture | string) {
    super(new ImageView({ canHandleEvents: false }));
    this.tag = 'Sprite';
    if (texture) {
      if (typeof texture === 'string') {
        this.texture = Texture.from(texture);
      } else {
        this.texture = texture;
      }
    }
  }

  public get texture() {
    return this._texture;
  }

  public set texture(v: Texture | null) {
    this._texture = v;
    this.updateTexture();
  }

  public updateTexture() {
    if (!this._texture) {
      this._innerView.updateOpts({ image: '' });
      return;
    }

    this._innerView.updateOpts({
      image: getMatch2AssetPath(this._texture.image),
    });
    this._innerView.autoSize();
    this.width = this._innerView.style.width;
    this.height = this._innerView.style.height;
    this.updateAnchor();

    // Update sprite tag, for debugging
    if (this.tag === 'Sprite' || this.tag.startsWith('Sprite:')) {
      if (this._texture) {
        this.tag = `Sprite:${this._texture.tag}`;
      } else {
        this.tag = `Sprite`;
      }
    }
  }

  public get tint() {
    return this._tint;
  }

  public set tint(v: number) {
    this._tint = v;
    if (v !== 0xffffff) {
      if (!this._tintFilter) {
        this._tintFilter = new MultiplyFilter(hex2string(v));
        this._innerView.updateOpts({ filter: this._tintFilter });
      }
      this._tintFilter.color.update(hex2string(v));
    } else {
      this._innerView.updateOpts({ filter: undefined });
      this._tintFilter = null;
    }
  }

  public get fill() {
    return this._fill ?? 0xffffff;
  }

  public set fill(v: number) {
    this._fill = v;
    if (v !== null) {
      if (!this._fillFilter) {
        this._fillFilter = new TintFilter(hex2string(v));
        this._innerView.updateOpts({ filter: this._fillFilter });
      }
      this._fillFilter.color.update(hex2string(v));
    } else {
      this._innerView.updateOpts({ filter: undefined });
      this._fillFilter = null;
    }
  }

  public get linearAdd() {
    return this._linearAdd ?? 0;
  }

  public set linearAdd(v: number) {
    this._linearAdd = v;
    if (v !== 0) {
      if (!this._linearAddFilter) {
        this._linearAddFilter = new LinearAddFilter(hex2string(v));
      }
      this._innerView.updateOpts({ filter: this._linearAddFilter });
      this._linearAddFilter.color.update(hex2string(v));
    } else {
      this._innerView.updateOpts({ filter: undefined });
      this._linearAddFilter = null;
    }
  }
}
